import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Save, Clock } from 'lucide-react';

const TIMEZONES = [
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'UTC', label: 'UTC' },
];

// Generate all 24 hours with locale-aware formatting
const generateReminderTimes = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    const value = hour.toString().padStart(2, '0') + ':00';
    const label = date.toLocaleTimeString([], { hour: 'numeric', hour12: true });
    times.push({ value, label });
  }
  return times;
};

const REMINDER_TIMES = generateReminderTimes();

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'nl', label: 'Nederlands' },
];

export default function SettingsPage() {
  const { user, logout, getAuthHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    timezone: 'Europe/Paris',
    email_reminders: true,
    reminder_time: '09:00',
    language: 'en',
    weekly_digest: false,
    weekly_digest_day: 'monday',
    weekly_digest_time: '07:00',
    advance_reminder: 'off'
  });
  const [reminderStatus, setReminderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API}/settings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/reminders/status`, { headers: getAuthHeaders() })
      ]);
      setSettings(settingsRes.data);
      setReminderStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: getAuthHeaders()
      });
      // Refresh reminder status after saving
      const statusRes = await axios.get(`${API}/reminders/status`, { headers: getAuthHeaders() });
      setReminderStatus(statusRes.data);
      toast.success('Settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: getAuthHeaders()
      });
      toast.success('Account deleted');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
      setDeleting(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `decided_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-medium tracking-tight hover:text-accent transition-colors">
            Decided
          </Link>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="rounded-sm"
            data-testid="save-settings-button"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-8 py-12">
        <div className="space-y-12 animate-fade-in">
          {/* Account Section */}
          <section>
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
              Account
            </h2>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="space-y-1">
                <p className="text-foreground font-medium">{user?.name}</p>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </section>

          {/* Timezone Section */}
          <section>
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
              Timezone
            </h2>
            <div className="bg-card border border-border rounded-lg p-6">
              <Label htmlFor="timezone" className="text-sm text-muted-foreground mb-3 block">
                Your local timezone for reminders
              </Label>
              <Select
                value={settings.timezone}
                onValueChange={(value) => setSettings({ ...settings, timezone: value })}
              >
                <SelectTrigger className="w-full" data-testid="timezone-select">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Language Section */}
          <section>
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
              Language
            </h2>
            <div className="bg-card border border-border rounded-lg p-6">
              <Label htmlFor="language" className="text-sm text-muted-foreground mb-3 block">
                Preferred language
              </Label>
              <Select
                value={settings.language}
                onValueChange={(value) => setSettings({ ...settings, language: value })}
              >
                <SelectTrigger className="w-full" data-testid="language-select">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground/70 mt-3">
                More languages coming soon.
              </p>
            </div>
          </section>

          {/* Email Preferences Section */}
          <section>
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
              Email Preferences
            </h2>
            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              {/* Email Reminders Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-foreground font-medium">Email Reminders</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Receive email reminders for follow-up actions on their due date
                  </p>
                </div>
                <Switch
                  checked={settings.email_reminders}
                  onCheckedChange={(checked) => setSettings({ ...settings, email_reminders: checked })}
                  data-testid="email-reminders-switch"
                />
              </div>

              {/* Reminder Time */}
              {settings.email_reminders && (
                <div className="pt-4 border-t border-border">
                  <Label htmlFor="reminder_time" className="text-sm text-muted-foreground mb-3 block">
                    Daily reminder time
                  </Label>
                  <Select
                    value={settings.reminder_time}
                    onValueChange={(value) => setSettings({ ...settings, reminder_time: value })}
                  >
                    <SelectTrigger className="w-full max-w-xs" data-testid="reminder-time-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {REMINDER_TIMES.map((time) => (
                        <SelectItem key={time.value} value={time.value}>
                          {time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Advance Reminder */}
              {settings.email_reminders && (
                <div className="pt-4">
                  <Label htmlFor="advance_reminder" className="text-sm text-muted-foreground mb-3 block">
                    Advance reminder
                  </Label>
                  <Select
                    value={settings.advance_reminder || 'off'}
                    onValueChange={(value) => setSettings({ ...settings, advance_reminder: value })}
                  >
                    <SelectTrigger className="w-full max-w-xs" data-testid="advance-reminder-select">
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="1_day">1 day before</SelectItem>
                      <SelectItem value="2_days">2 days before</SelectItem>
                      <SelectItem value="1_week">1 week before</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground/50 mt-2">
                    Get reminded before the due date.
                  </p>
                </div>
              )}

              {/* Weekly Digest Toggle */}
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <div>
                  <p className="text-foreground font-medium">Weekly digest</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Get a weekly email with your upcoming follow-ups
                  </p>
                </div>
                <Switch
                  checked={settings.weekly_digest || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, weekly_digest: checked })}
                  data-testid="weekly-digest-switch"
                />
              </div>

              {/* Weekly Digest Options */}
              {settings.weekly_digest && (
                <div className="pt-4 space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-3 block">Day</Label>
                    <Select
                      value={settings.weekly_digest_day || 'monday'}
                      onValueChange={(value) => setSettings({ ...settings, weekly_digest_day: value })}
                    >
                      <SelectTrigger className="w-full max-w-xs" data-testid="weekly-digest-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monday">Monday</SelectItem>
                        <SelectItem value="sunday">Sunday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-3 block">Time</Label>
                    <Select
                      value={settings.weekly_digest_time || '07:00'}
                      onValueChange={(value) => setSettings({ ...settings, weekly_digest_time: value })}
                    >
                      <SelectTrigger className="w-full max-w-xs" data-testid="weekly-digest-time-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_TIMES.map((time) => (
                          <SelectItem key={time.value} value={time.value}>
                            {time.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground/70">
                    If there's nothing upcoming, no email is sent.
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Info Note */}
          <section className="text-center text-sm text-muted-foreground/70 pt-8">
            <p>Reminders are sent once per follow-up action, on the due date.</p>
          </section>

          {/* Reminder Status */}
          {reminderStatus && settings.email_reminders && (
            <section className="animate-fade-in">
              <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
                Reminder Status
              </h2>
              <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Clock size={16} />
                  <span className="text-sm">
                    Your time: <span className="text-foreground">{reminderStatus.current_time_user?.split(' ').slice(1).join(' ')}</span>
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Next reminder check: <span className="text-foreground">{reminderStatus.next_reminder_check}</span>
                </div>
                {reminderStatus.pending_reminders_today?.length > 0 && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-sm text-foreground font-medium mb-2">
                      {reminderStatus.pending_reminders_today.length} reminder{reminderStatus.pending_reminders_today.length > 1 ? 's' : ''} pending today:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {reminderStatus.pending_reminders_today.map((r, i) => (
                        <li key={i} className={r.reminder_sent ? 'line-through' : ''}>
                          • {r.action} <span className="text-muted-foreground/70">({r.decision_title})</span>
                          {r.reminder_sent && <span className="text-xs ml-2 text-green-600">✓ sent</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!reminderStatus.email_service_configured && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-amber-600">
                      Note: Email service not yet configured. Reminders will be sent once activated.
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Export Data Section */}
          <section className="pt-12 mt-12 border-t border-border">
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
              Export my data
            </h2>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-foreground font-medium mb-2">
                Download a copy of your decisions and follow-ups
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Exports all your data in a simple spreadsheet format.
              </p>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={exporting}
                data-testid="export-csv-button"
              >
                {exporting ? 'Exporting...' : 'Download CSV'}
              </Button>
            </div>
          </section>

          {/* Delete Account Section */}
          <section className="pt-12 mt-12 border-t border-border">
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
              Delete Account
            </h2>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-foreground font-medium mb-2">
                Permanently delete your account
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                This will permanently remove your account and all associated data, including decisions, follow-ups, and settings. This action cannot be undone.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
                data-testid="delete-account-button"
              >
                Delete account
              </Button>
            </div>
          </section>
        </div>
      </main>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your account and all your data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-account"
            >
              {deleting ? 'Deleting...' : 'Delete account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
